import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, CertificationExamWithStatus, Certificate, CertificateSummary } from "../lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Award,
  Clock,
  Target,
  Lock,
  CheckCircle,
  ChevronRight,
  Shield,
  Star,
  Trophy,
} from "lucide-react";

type TabType = "available" | "my-certificates";

const EXAM_TYPE_LABELS: Record<string, string> = {
  technique: "Technique Certificate",
  category: "Category Certificate",
  platform: "Ithopu Certification",
};

const PLATFORM_LEVEL_NAMES: Record<number, string> = {
  1: "Foundations",
  2: "Intermediate",
  3: "Advanced",
  4: "Master",
};

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

interface UnlockRequirement {
  icon: React.ReactNode;
  label: string;
  met: boolean;
}

function getUnlockRequirements(exam: CertificationExamWithStatus): UnlockRequirement[] {
  const reqs: UnlockRequirement[] = [];

  if (exam.min_user_level > 1) {
    const reason = exam.eligibility_reason || "";
    const levelMet = !reason.includes("Requires Level");
    reqs.push({
      icon: <Star className="w-3 h-3" />,
      label: `Level ${exam.min_user_level}+`,
      met: levelMet,
    });
  }

  if (exam.min_mastery_level) {
    const reason = exam.eligibility_reason || "";
    const masteryMet = !reason.includes("mastery");
    reqs.push({
      icon: <Target className="w-3 h-3" />,
      label: `${exam.min_mastery_level.charAt(0).toUpperCase() + exam.min_mastery_level.slice(1)} mastery`,
      met: masteryMet,
    });
  }

  if (exam.required_certificate_count > 0) {
    const reason = exam.eligibility_reason || "";
    const certsMet = !reason.includes("prerequisite certificate");
    reqs.push({
      icon: <Award className="w-3 h-3" />,
      label: `${exam.required_certificate_count} prerequisite cert${exam.required_certificate_count !== 1 ? "s" : ""}`,
      met: certsMet,
    });
  }

  reqs.push({
    icon: <CheckCircle className="w-3 h-3" />,
    label: `${exam.passing_accuracy}% accuracy`,
    met: false,
  });

  return reqs;
}

function ExamCard({
  exam,
  onStart,
}: {
  exam: CertificationExamWithStatus;
  onStart: (id: string) => void;
}) {
  const isCertified = exam.is_certified;
  const isLocked = !exam.eligible;

  return (
    <Card
      className={`transition-all ${
        isCertified
          ? "border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950"
          : isLocked
          ? "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900 opacity-75"
          : "hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600"
      }`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {isCertified ? (
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              ) : isLocked ? (
                <Lock className="w-5 h-5 text-gray-400 flex-shrink-0" />
              ) : (
                <Award className="w-5 h-5 text-blue-600 flex-shrink-0" />
              )}
              <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                {exam.name}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
              {exam.description}
            </p>
            <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <Target className="w-3 h-3" />
                {exam.problem_count} problems
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatTime(exam.time_limit_seconds)}
              </span>
              <span className="flex items-center gap-1">
                <Star className="w-3 h-3" />
                {exam.passing_accuracy}% to pass
              </span>
            </div>
            {exam.total_attempts > 0 && !isCertified && (
              <p className="text-xs text-gray-500 mt-2">
                {exam.total_attempts} attempt{exam.total_attempts !== 1 ? "s" : ""} &middot; Best: {exam.best_accuracy?.toFixed(1)}%
              </p>
            )}
            {isCertified && exam.certificate && (
              <p className="text-xs text-green-700 dark:text-green-400 mt-2 font-medium">
                Certified &middot; {exam.certificate.certificate_number}
              </p>
            )}
            {isLocked && exam.eligibility_reason && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                {exam.eligibility_reason}
              </p>
            )}
            {isLocked && (
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">What you need:</p>
                <div className="flex flex-wrap gap-1.5">
                  {getUnlockRequirements(exam).map((req, i) => (
                    <span
                      key={i}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        req.met
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                      }`}
                    >
                      {req.icon}
                      {req.label}
                      {req.met && <CheckCircle className="w-3 h-3" />}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex-shrink-0">
            {isCertified ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                Earned
              </span>
            ) : isLocked ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                Locked
              </span>
            ) : (
              <Button
                size="sm"
                onClick={() => onStart(exam.id)}
                className="flex items-center gap-1"
              >
                Take Exam
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CertificateCard({ cert }: { cert: Certificate }) {
  const navigate = useNavigate();

  return (
    <Card
      className="border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950 dark:to-yellow-950 dark:border-amber-800 cursor-pointer hover:shadow-lg transition-all"
      onClick={() => navigate(`/certificate/${cert.id}`)}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
            {cert.certificate_type === "platform" ? (
              <Trophy className="w-6 h-6 text-amber-600" />
            ) : cert.certificate_type === "category" ? (
              <Shield className="w-6 h-6 text-amber-600" />
            ) : (
              <Award className="w-6 h-6 text-amber-600" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {cert.title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {EXAM_TYPE_LABELS[cert.certificate_type]}
              {cert.certificate_level
                ? ` - Level ${cert.certificate_level}: ${PLATFORM_LEVEL_NAMES[cert.certificate_level] || ""}`
                : ""}
            </p>
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
              <span>{cert.accuracy.toFixed(1)}% accuracy</span>
              <span>{cert.problems_correct}/{cert.problems_attempted} correct</span>
              <span>{new Date(cert.issued_at).toLocaleDateString()}</span>
            </div>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 font-mono">
              {cert.certificate_number}
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-2" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function Certifications() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("available");
  const [exams, setExams] = useState<CertificationExamWithStatus[]>([]);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [summary, setSummary] = useState<CertificateSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [examTypeFilter, setExamTypeFilter] = useState<string>("all");

  useEffect(() => {
    const loadData = async () => {
      try {
        const [examsData, certsData] = await Promise.all([
          api.getCertificationExams(),
          api.getUserCertificates(),
        ]);
        setExams(examsData);
        setCertificates(certsData.certificates);
        setSummary(certsData.summary);
      } catch (error) {
        console.error("Failed to load certification data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleStartExam = (examId: string) => {
    navigate(`/certification-exam/${examId}`);
  };

  const filteredExams = exams.filter((e) => {
    if (examTypeFilter === "all") return true;
    return e.exam_type === examTypeFilter;
  });

  const techniqueExams = filteredExams.filter((e) => e.exam_type === "technique");
  const categoryExams = filteredExams.filter((e) => e.exam_type === "category");
  const platformExams = filteredExams.filter((e) => e.exam_type === "platform");

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Award className="w-8 h-8 text-blue-600" />
          Certifications
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Prove your mental math skills. Earn certificates that you can share and verify.
        </p>
      </div>

      {/* Summary Cards */}
      {summary && summary.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{summary.total}</p>
              <p className="text-xs text-gray-500">Total Certificates</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{summary.technique_count}</p>
              <p className="text-xs text-gray-500">Technique</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{summary.category_count}</p>
              <p className="text-xs text-gray-500">Category</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{summary.platform_count}</p>
              <p className="text-xs text-gray-500">Platform</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab("available")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "available"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Available Exams
        </button>
        <button
          onClick={() => setActiveTab("my-certificates")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "my-certificates"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          My Certificates ({summary?.total || 0})
        </button>
      </div>

      {/* Available Exams Tab */}
      {activeTab === "available" && (
        <div>
          {/* Filter */}
          <div className="flex gap-2 mb-6 flex-wrap">
            {["all", "technique", "category", "platform"].map((type) => (
              <Button
                key={type}
                variant={examTypeFilter === type ? "default" : "outline"}
                size="sm"
                onClick={() => setExamTypeFilter(type)}
              >
                {type === "all"
                  ? "All"
                  : type === "technique"
                  ? "Technique"
                  : type === "category"
                  ? "Category"
                  : "Platform"}
              </Button>
            ))}
          </div>

          {/* Platform Certifications */}
          {platformExams.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Ithopu Certification Levels
              </h2>
              <div className="space-y-3">
                {platformExams
                  .sort((a, b) => (a.certification_level || 0) - (b.certification_level || 0))
                  .map((exam) => (
                    <ExamCard key={exam.id} exam={exam} onStart={handleStartExam} />
                  ))}
              </div>
            </div>
          )}

          {/* Category Certifications */}
          {categoryExams.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-500" />
                Category Certificates
              </h2>
              <div className="space-y-3">
                {categoryExams.map((exam) => (
                  <ExamCard key={exam.id} exam={exam} onStart={handleStartExam} />
                ))}
              </div>
            </div>
          )}

          {/* Technique Certifications */}
          {techniqueExams.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-blue-500" />
                Technique Certificates
              </h2>
              <div className="space-y-3">
                {techniqueExams.map((exam) => (
                  <ExamCard key={exam.id} exam={exam} onStart={handleStartExam} />
                ))}
              </div>
            </div>
          )}

          {filteredExams.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Award className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No certification exams available for this filter.</p>
            </div>
          )}
        </div>
      )}

      {/* My Certificates Tab */}
      {activeTab === "my-certificates" && (
        <div>
          {certificates.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Award className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="mb-2">No certificates earned yet.</p>
              <p className="text-sm">
                Take a certification exam to earn your first certificate!
              </p>
              <Button
                className="mt-4"
                onClick={() => setActiveTab("available")}
              >
                Browse Exams
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {certificates.map((cert) => (
                <CertificateCard key={cert.id} cert={cert} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
